import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, Target, TrendingUp } from 'lucide-react';
import { useVocabulary } from '../context/VocabularyContext';
import { srsManager } from '../utils/srsManager';

export function CategoriesGrid() {
  const { state, dispatch, actions } = useVocabulary();
  const { vocabularyData } = state;
  const [categoryStats, setCategoryStats] = useState({});

  // Load SRS statistics for all categories
  useEffect(() => {
    const grammarCategories = vocabularyData?.grammar_categories || {};
    const pools = vocabularyData?.pools || {};
    
    if (grammarCategories && pools) {
      const stats = {};
      Object.entries(grammarCategories).forEach(([categoryKey, categoryData]) => {
        // Aggregate stats for all pools in this category
        let aggregatedStats = {
          total: 0,
          dueForReview: 0,
          tomorrow: 0,
          mastered: 0
        };
        
        if (categoryData.pools) {
          categoryData.pools.forEach(poolCode => {
            const poolData = pools[poolCode];
            const wordCount = poolData?.metadata?.word_count || poolData?.words?.length || 0;
            const poolStat = srsManager.getPoolStats('words', poolCode, wordCount);
            aggregatedStats.total += poolStat.total;
            aggregatedStats.dueForReview += poolStat.dueForReview;
            aggregatedStats.tomorrow += poolStat.tomorrow;
            aggregatedStats.mastered += poolStat.mastered;
          });
        }
        
        stats[categoryKey] = aggregatedStats;
      });
      setCategoryStats(stats);
    }
  }, [vocabularyData]);
  
  const grammarCategories = vocabularyData?.grammar_categories || {};

  // Handle category click - navigate to pools view
  const handleCategoryClick = (categoryKey) => {
    dispatch({ type: actions.SET_SELECTED_CATEGORY, payload: categoryKey });
    dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'word-pools' });
  };

  // Handle back to exercise type selector
  const handleBackToExerciseTypes = () => {
    dispatch({ type: actions.SET_CURRENT_VIEW, payload: 'exercise-type-selector' });
  };

  // Get category icon based on type
  const getCategoryIcon = (categoryKey) => {
    const icons = {
      'pronouns': '👤',
      'verbs': '⚡',
      'nouns': '📝',
      'adjectives': '🎨',
      'adverbs': '🕐',
      'numbers': '🔢',
      'calendar': '📅',
      'connectors': '↔️'
    };
    return icons[categoryKey] || '📚';
  };

  // Get category color
  const getCategoryColor = (categoryKey) => {
    const colors = {
      'pronouns': 'blue',
      'verbs': 'purple',
      'nouns': 'green',
      'adjectives': 'orange',
      'adverbs': 'pink',
      'numbers': 'indigo',
      'calendar': 'yellow',
      'connectors': 'teal'
    };
    return colors[categoryKey] || 'blue';
  };

  // Sort categories by order
  const sortedCategories = Object.entries(grammarCategories)
    .sort(([,a], [,b]) => (a.order || 999) - (b.order || 999));

  // Color classes for categories
  const colorClasses = {
    blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300',
    purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 hover:border-purple-300',
    green: 'bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300',
    orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 hover:border-orange-300',
    pink: 'bg-pink-50 hover:bg-pink-100 border-pink-200 hover:border-pink-300',
    indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 hover:border-indigo-300',
    yellow: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 hover:border-yellow-300',
    teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200 hover:border-teal-300'
  };

  const iconBgClasses = {
    blue: 'bg-blue-600',
    purple: 'bg-purple-600',
    green: 'bg-green-600',
    orange: 'bg-orange-600',
    pink: 'bg-pink-600',
    indigo: 'bg-indigo-600',
    yellow: 'bg-yellow-600',
    teal: 'bg-teal-600'
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={handleBackToExerciseTypes}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          title="Powrót do wyboru typu ćwiczenia"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Wybierz kategorię gramatyczną
          </h1>
          <p className="text-gray-600">
            Każda kategoria zawiera dedykowane pule słownictwa
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {sortedCategories.map(([categoryKey, category]) => {
          const color = getCategoryColor(categoryKey);
          const poolCount = category.pools?.length || 0;

          return (
            <button
              key={categoryKey}
              onClick={() => handleCategoryClick(categoryKey)}
              className={`p-6 ${colorClasses[color]} border-2 rounded-xl transition-all duration-200 text-left group`}
            >
              {/* Category Header */}
              <div className="flex items-center mb-4">
                <div className={`p-3 ${iconBgClasses[color]} text-white rounded-lg text-2xl group-hover:scale-110 transition-transform`}>
                  {getCategoryIcon(categoryKey)}
                </div>
                <h3 className="text-xl font-bold text-gray-800 ml-4">
                  {category.name}
                </h3>
              </div>

              {/* Pool Count */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Dostępne pule:</span>
                  <span className="bg-white text-gray-700 px-3 py-1 rounded-full font-medium">
                    {poolCount}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {category.pools?.slice(0, 3).map(poolCode => (
                    <span key={poolCode} className="text-xs bg-white text-gray-600 px-2 py-1 rounded border">
                      {poolCode}
                    </span>
                  ))}
                  {poolCount > 3 && (
                    <span className="text-xs bg-white text-gray-500 px-2 py-1 rounded border">
                      +{poolCount - 3} więcej
                    </span>
                  )}
                </div>
              </div>

              {/* SRS Stats */}
              {categoryStats[categoryKey] && (
                <div className="p-3 bg-white/70 rounded-lg border border-gray-200">
                  <div className="text-sm font-medium text-gray-700 mb-2">Postęp uczenia:</div>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-orange-600 mb-1">
                        <Clock size={14} />
                        <span className="font-semibold text-sm">{categoryStats[categoryKey].dueForReview}</span>
                      </div>
                      <div className="text-gray-600">Do powtórki</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                        <Target size={14} />
                        <span className="font-semibold text-sm">{categoryStats[categoryKey].tomorrow}</span>
                      </div>
                      <div className="text-gray-600">Na jutro</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                        <TrendingUp size={14} />
                        <span className="font-semibold text-sm">{categoryStats[categoryKey].mastered}</span>
                      </div>
                      <div className="text-gray-600">Opanowane</div>
                    </div>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading/Empty state */}
      {sortedCategories.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500">
            <span className="text-4xl block mb-2">📚</span>
            Ładowanie kategorii...
          </div>
        </div>
      )}
    </div>
  );
} 